import {useState, useEffect} from "react";
import {Box, Button, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import {Navigate, useParams, useNavigate} from "react-router";
import {appSession} from "../../../TR3AD.ts";
import WidgetHeader from "../../../widgets/WidgetHeader.tsx";
import {appStyles} from "../../../AppStyles.ts";
import {StyledSearch, StyledSearchIconWrapper, StyledInputBase} from "../../AppStyledComponents.tsx";
import type {GraphSettings} from "../../../sdo/GraphSettings.ts";
import GraphEditForm from "./GraphEditForm.tsx";

// TODO: Remove once login flow is wired and appSession.getUsername() returns a real value in dev.
const DEV_USER = "dev-user";

// TODO: Replace with API call to GraphManager.readAllGraphSettings()
function buildTestData(currentUser: string): GraphSettings[] {
    return [
        {uid: "graph-1", createdBy: currentUser, title: "Entity Network - Q1 2026", layout: "FORCE", saveNodePositions: true, rootNodes: [{uid: "rn-1", display: "John Marcus Riley", typeKey: "PERSON", profileType: "INTEL"}, {uid: "rn-2", display: "Sentinel Group Holdings", typeKey: "ORGANIZATION", profileType: "INTEL"}], fontOverrideEnabled: false, fontFamily: "Montserrat", fontSize: 12, fontBold: false, fontItalic: false, fontUnderline: false, fontColorHex: "#FFFFFF", rootNodeColorEnabled: false, rootNodeColorHex: "#02cfee", resourceNodeColorEnabled: false, resourceNodeColorHex: "#38ff82", intelNodeColorEnabled: false, intelNodeColorHex: "#02cfee", edgeStyleEnabled: false, edgeColorHex: "#808080", edgeStrokeWidth: 1.0, edgeStrokeStyle: "SOLID"},
        {uid: "graph-2", createdBy: currentUser, title: "Maritime Corridor Analysis", layout: "RADIAL", saveNodePositions: false, rootNodes: [{uid: "rn-3", display: "Port of Rotterdam", typeKey: "PLACE", profileType: "INTEL"}, {uid: "rn-4", display: "Cargo Manifest Records", typeKey: "RECORDS", profileType: "RESOURCE"}, {uid: "rn-5", display: "Meridian Logistics", typeKey: "ORGANIZATION", profileType: "INTEL"}], fontOverrideEnabled: false, fontFamily: "Montserrat", fontSize: 12, fontBold: false, fontItalic: false, fontUnderline: false, fontColorHex: "#FFFFFF", rootNodeColorEnabled: false, rootNodeColorHex: "#02cfee", resourceNodeColorEnabled: false, resourceNodeColorHex: "#38ff82", intelNodeColorEnabled: false, intelNodeColorHex: "#02cfee", edgeStyleEnabled: false, edgeColorHex: "#808080", edgeStrokeWidth: 1.0, edgeStrokeStyle: "SOLID"},
        {uid: "graph-3", createdBy: "K. Andersen", title: "Signal Intelligence Links", layout: "FORCE", saveNodePositions: true, rootNodes: [{uid: "rn-6", display: "Signal Intercept Alpha-7", typeKey: "FILES", profileType: "RESOURCE"}], fontOverrideEnabled: false, fontFamily: "Montserrat", fontSize: 12, fontBold: false, fontItalic: false, fontUnderline: false, fontColorHex: "#FFFFFF", rootNodeColorEnabled: false, rootNodeColorHex: "#02cfee", resourceNodeColorEnabled: false, resourceNodeColorHex: "#38ff82", intelNodeColorEnabled: false, intelNodeColorHex: "#02cfee", edgeStyleEnabled: false, edgeColorHex: "#808080", edgeStrokeWidth: 1.0, edgeStrokeStyle: "SOLID"},
    ];
}

// --- Card styles ---

const graphCardSx = {
    background: "linear-gradient(135deg, rgba(9,25,32,0.85) 0%, rgba(20,40,55,0.7) 100%)",
    border: "1px solid #3F464F",
    borderRadius: "12px",
    padding: "12px 16px",
    width: 320,
    minWidth: 280,
    cursor: "pointer",
    "&:hover": {
        borderColor: "rgba(2, 207, 238, 0.45)",
        backgroundColor: "rgba(2, 207, 238, 0.04)",
    },
};

const cardTitleSx = {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#F2F5FA",
    textAlign: "left" as const,
};

const cardLabelSx = {
    fontSize: "0.65rem",
    fontWeight: 400,
    color: "#a8b8c0",
    textAlign: "left" as const,
};

// --- Card ---

function GraphCard({graph, onClick, onEdit}: { graph: GraphSettings; onClick: () => void; onEdit?: () => void }) {
    const [hovered, setHovered] = useState(false);

    return (
        <Box
            sx={{...graphCardSx, position: "relative"}}
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {onEdit && (
                <Tooltip title="Edit graph settings">
                    <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onEdit(); }}
                        sx={{
                            position: "absolute", top: 6, right: 6,
                            opacity: hovered ? 1 : 0, transition: "opacity 0.15s",
                            padding: "4px", color: "#7093a0",
                            "&:hover": {color: "#02cfee"},
                        }}
                    >
                        <EditIcon sx={{fontSize: 16}} />
                    </IconButton>
                </Tooltip>
            )}

            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box component="img" src="/images/graph.svg" sx={{width: 32, height: 32, opacity: 0.6, mt: "2px", flexShrink: 0}}
                     onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = "none"; }}
                />
                <Stack spacing={0.3} sx={{minWidth: 0, flex: 1}}>
                    <Typography sx={cardTitleSx}>{graph.title}</Typography>
                    <Typography sx={cardLabelSx}>Nodes: {graph.rootNodes.length} · Layout: {graph.layout}</Typography>
                    <Typography sx={cardLabelSx}>by {graph.createdBy}</Typography>
                </Stack>
            </Stack>
        </Box>
    );
}

// --- Main component ---

function UXGraphSelect() {
    const {networkUID} = useParams();
    const navigate = useNavigate();
    const [graphs, setGraphs] = useState<GraphSettings[]>([]);
    const [filterText, setFilterText] = useState("");
    const [editingUID, setEditingUID] = useState<string | null>(null);

    // TODO: Drop DEV_USER fallback once appSession.getUsername() returns a real value in dev.
    const currentUser = appSession.getUsername() ?? DEV_USER;

    useEffect(() => {
        // TODO: Replace with API call
        setGraphs(buildTestData(currentUser));
    }, [networkUID, currentUser]);

    const filtered = graphs.filter(g => {
        const q = filterText.trim().toLowerCase();
        if (!q) return true;
        return g.title.toLowerCase().includes(q);
    });

    function handleUpdateGraph(graph: GraphSettings) {
        // TODO: Replace with API call to update graph (createdBy unchanged)
        setGraphs(prev => prev.map(g => g.uid === graph.uid ? graph : g));
        setEditingUID(null);
    }

    function handleDeleteGraph(uid: string) {
        // TODO: Replace with API call to delete graph
        setGraphs(prev => prev.filter(g => g.uid !== uid));
        setEditingUID(null);
    }

    return (
        <Stack direction="column" width="100%" minHeight="100vh">
            {!appSession.activeSession() ? (
                <Navigate to="/" />
            ) : (
                <Stack direction="column" spacing={1} width="99%">
                    <WidgetHeader
                        title="Graphs"
                        breadcrumbs={[
                            {display: "Networks", linkTo: "/networks/", activeView: false},
                            {display: String(networkUID), linkTo: `/networks/${networkUID}`, activeView: false},
                            {display: "Graphs", linkTo: "", activeView: true},
                        ]}
                    />
                    <Stack sx={appStyles.sectionBox} spacing={0} width="100%" alignItems="center">
                        <Typography sx={{...appStyles.panelSectionTitle, padding: "16px 0 8px"}}>
                            Graphs
                        </Typography>

                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{padding: "0 16px 12px"}} flexWrap="wrap">
                            <StyledSearch>
                                <StyledSearchIconWrapper>
                                    <img src="/images/search_white.svg" width={16} />
                                </StyledSearchIconWrapper>
                                <StyledInputBase
                                    placeholder="Filter"
                                    value={filterText}
                                    onChange={(e) => setFilterText(e.target.value)}
                                    inputProps={{"aria-label": "filter"}}
                                />
                            </StyledSearch>
                            <Typography sx={appStyles.showCountText}>
                                Showing {filtered.length} of {graphs.length}
                            </Typography>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => navigate(`/networks/${networkUID}/graphs/new`)}
                                sx={appStyles.panelActionButton}
                            >
                                Create
                            </Button>
                        </Stack>

                        {filtered.length > 0 ? (
                            <Box sx={{display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "center", padding: "8px 16px 16px"}}>
                                {filtered.map(graph => {
                                    const canEdit = currentUser !== null && graph.createdBy === currentUser;
                                    if (editingUID === graph.uid) {
                                        return (
                                            <Box key={graph.uid} sx={{width: "100%", display: "flex", justifyContent: "center"}}>
                                                <GraphEditForm
                                                    initial={graph}
                                                    onSave={handleUpdateGraph}
                                                    onCancel={() => setEditingUID(null)}
                                                    onDelete={() => handleDeleteGraph(graph.uid)}
                                                />
                                            </Box>
                                        );
                                    }
                                    return (
                                        <GraphCard
                                            key={graph.uid}
                                            graph={graph}
                                            onClick={() => navigate(`/networks/${networkUID}/graphs/${graph.uid}`)}
                                            onEdit={canEdit ? () => setEditingUID(graph.uid) : undefined}
                                        />
                                    );
                                })}
                            </Box>
                        ) : (
                            <Typography sx={{color: "#a8b8c0", fontSize: "0.85rem", padding: "32px 0"}}>
                                No graphs found.
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}

export default UXGraphSelect;
